import React, { useEffect, useState } from 'react';

function PmsCmMapping({ token, selectedProperty }) {
  const propertyId = selectedProperty?.id || '';

  const [pmsRoomTypes, setPmsRoomTypes] = useState([]);
  const [pmsRatePlans, setPmsRatePlans] = useState([]);

  // EaseRoom data — loaded once, shared across all rows
  const [erRoomTypes, setErRoomTypes] = useState([]);
  const [erRatePlans, setErRatePlans] = useState([]);
  const [loadingErRt, setLoadingErRt] = useState(false);
  const [loadingErRp, setLoadingErRp] = useState(false);

  // Which row is currently open (one at a time)
  const [activeRtId, setActiveRtId] = useState(null);
  const [activeRpId, setActiveRpId] = useState(null);

  // Draft selection for the currently open row
  const [rtDraft, setRtDraft] = useState({ easeroom_room_type_id: '', easeroom_room_type_name: '' });
  const [rpDraft, setRpDraft] = useState({ easeroom_rate_plan_id: '', easeroom_rate_plan_name: '', easeroom_room_type_id: '' });

  const [loadingPms, setLoadingPms] = useState(false);
  const [savingRtId, setSavingRtId] = useState(null);
  const [savingRpId, setSavingRpId] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const headers = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}` });

  const fmtDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // ---- Load PMS data ----
  const loadPmsMappings = async () => {
    if (!propertyId) return;
    setLoadingPms(true);
    setError('');
    try {
      const [rtRes, rpRes] = await Promise.all([
        fetch(`/api/v1/channel-manager/mappings/pms-room-types?property_id=${propertyId}`, { headers: headers() }),
        fetch(`/api/v1/channel-manager/mappings/pms-rate-plans?property_id=${propertyId}`, { headers: headers() })
      ]);
      const [rtData, rpData] = await Promise.all([rtRes.json(), rpRes.json()]);
      if (!rtRes.ok) { setError(rtData?.error || 'Failed to load room types.'); return; }
      if (!rpRes.ok) { setError(rpData?.error || 'Failed to load rate plans.'); return; }
      setPmsRoomTypes(rtData.data || []);
      setPmsRatePlans(rpData.data || []);
    } catch {
      setError('Failed to load PMS data. Please try again.');
    } finally {
      setLoadingPms(false);
    }
  };

  useEffect(() => {
    loadPmsMappings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  // ---- Load EaseRoom Room Types — returns the list so callers can use it immediately ----
  const fetchErRoomTypes = async () => {
    if (!propertyId) { setError('No property selected.'); return []; }
    setLoadingErRt(true);
    setError('');
    try {
      const res = await fetch(`/api/v1/channel-manager/room-types?property_id=${propertyId}`, { headers: headers() });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to load EaseRoom room types.'); return []; }
      const rtKey = Object.keys(data).find(k => k.toLowerCase() === 'roomtypes')
        || Object.keys(data).find(k => Array.isArray(data[k]));
      const list = rtKey ? (data[rtKey] || []) : [];
      setErRoomTypes(list);
      if (list.length === 0) {
        const keys = Object.keys(data).join(', ');
        setError(`EaseRoom returned no room types. Response keys: [${keys}]`);
      }
      return list;
    } catch {
      setError('Network error loading EaseRoom room types.');
      return [];
    } finally {
      setLoadingErRt(false);
    }
  };

  // ---- Load EaseRoom Rate Plans — returns the list so callers can use it immediately ----
  const fetchErRatePlans = async () => {
    if (!propertyId) { setError('No property selected.'); return []; }
    setLoadingErRp(true);
    setError('');
    try {
      const res = await fetch(`/api/v1/channel-manager/rate-plans?property_id=${propertyId}`, { headers: headers() });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to load EaseRoom rate plans.'); return []; }
      // Try common key variants; fallback: first array-valued key in the response
      const rpKey = Object.keys(data).find(k => k.toLowerCase() === 'rateplans')
        || Object.keys(data).find(k => Array.isArray(data[k]));
      const list = rpKey ? (data[rpKey] || []) : [];
      setErRatePlans(list);
      if (list.length === 0) {
        const keys = Object.keys(data).join(', ');
        setError(`EaseRoom returned no rate plans. Response keys: [${keys}]`);
      }
      return list;
    } catch {
      setError('Network error loading EaseRoom rate plans.');
      return [];
    } finally {
      setLoadingErRp(false);
    }
  };

  // ---- Open a Room Type row ----
  const openRtRow = async (rt) => {
    setActiveRpId(null);
    setError('');
    setMessage('');

    if (activeRtId === rt.room_type_id) {
      setActiveRtId(null);
      return;
    }

    // Use cached list or fetch fresh
    const list = erRoomTypes.length > 0 ? erRoomTypes : await fetchErRoomTypes();
    if (list.length === 0) return;

    setActiveRtId(rt.room_type_id);
    setRtDraft({
      easeroom_room_type_id: rt.easeroom_room_type_id ? String(rt.easeroom_room_type_id) : '',
      easeroom_room_type_name: rt.easeroom_room_type_name || ''
    });
  };

  // ---- Open a Rate Plan row ----
  const openRpRow = async (rp) => {
    setActiveRtId(null);
    setError('');
    setMessage('');

    if (activeRpId === rp.rate_plan_id) {
      setActiveRpId(null);
      return;
    }

    const list = erRatePlans.length > 0 ? erRatePlans : await fetchErRatePlans();
    if (list.length === 0) return;

    setActiveRpId(rp.rate_plan_id);
    setRpDraft({
      easeroom_rate_plan_id: rp.easeroom_rate_plan_id ? String(rp.easeroom_rate_plan_id) : '',
      easeroom_rate_plan_name: rp.easeroom_rate_plan_name || '',
      easeroom_room_type_id: rp.easeroom_room_type_id ? String(rp.easeroom_room_type_id) : ''
    });
  };

  // ---- Save single Room Type mapping ----
  const saveRtMapping = async (roomTypeId) => {
    if (!rtDraft.easeroom_room_type_id) { setError('Please select an EaseRoom Room Type.'); return; }
    setSavingRtId(roomTypeId);
    setError('');
    try {
      const res = await fetch('/api/v1/channel-manager/mappings/room-types', {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          property_id: propertyId,
          mappings: [{
            room_type_id: roomTypeId,
            easeroom_room_type_id: Number(rtDraft.easeroom_room_type_id),
            easeroom_room_type_name: rtDraft.easeroom_room_type_name || null
          }]
        })
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to save.'); return; }
      setMessage('Room type mapping saved.');
      setActiveRtId(null);
      loadPmsMappings();
    } catch {
      setError('Network error while saving.');
    } finally {
      setSavingRtId(null);
    }
  };

  // ---- Save single Rate Plan mapping ----
  const saveRpMapping = async (ratePlanId) => {
    if (!rpDraft.easeroom_rate_plan_id) { setError('Please select an EaseRoom Rate Plan.'); return; }
    setSavingRpId(ratePlanId);
    setError('');
    try {
      const res = await fetch('/api/v1/channel-manager/mappings/rate-plans', {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          property_id: propertyId,
          mappings: [{
            rate_plan_id: ratePlanId,
            easeroom_rate_plan_id: Number(rpDraft.easeroom_rate_plan_id),
            easeroom_rate_plan_name: rpDraft.easeroom_rate_plan_name || null,
            easeroom_room_type_id: rpDraft.easeroom_room_type_id ? Number(rpDraft.easeroom_room_type_id) : null
          }]
        })
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to save.'); return; }
      setMessage('Rate plan mapping saved.');
      setActiveRpId(null);
      loadPmsMappings();
    } catch {
      setError('Network error while saving.');
    } finally {
      setSavingRpId(null);
    }
  };

  // Rate plans filtered by the EaseRoom room type linked to a given PMS rate plan's room type
  const getFilteredErRatePlans = (rp) => {
    const rtRow = pmsRoomTypes.find(rt => rt.room_type_id === rp.room_type_id);
    const erRtId = rtRow?.easeroom_room_type_id;
    if (!erRtId) return erRatePlans; // show all if room type not yet mapped
    return erRatePlans.filter(r => String(r.RoomTypeId) === String(erRtId));
  };

  if (!propertyId) {
    return (
      <div className="page-shell">
        <div style={{ color: '#6b7280', padding: '2rem' }}>Please select a property to manage channel manager mappings.</div>
      </div>
    );
  }

  // ---- Styles ----
  const sectionStyle = {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '1.5rem',
    marginBottom: '1.5rem'
  };
  const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' };
  const thStyle = {
    background: 'var(--theme-bg-light)',
    padding: '0.6rem 1rem',
    textAlign: 'left',
    fontWeight: 600,
    fontSize: '0.78rem',
    color: 'var(--theme-text)',
    borderBottom: '2px solid #e5e7eb'
  };
  const tdStyle = { padding: '0.6rem 1rem', borderBottom: '1px solid var(--theme-border-light)', verticalAlign: 'middle' };
  const selectStyle = {
    padding: '0.4rem 0.65rem',
    borderRadius: '5px',
    border: '1px solid #d1d5db',
    fontSize: '0.85rem',
    minWidth: '260px',
    background: '#fff'
  };
  const badgeMapped = {
    display: 'inline-block',
    background: '#d1fae5',
    color: '#065f46',
    fontWeight: 600,
    fontSize: '0.72rem',
    padding: '0.15rem 0.5rem',
    borderRadius: '999px'
  };
  const badgeUnmapped = {
    display: 'inline-block',
    background: '#f3f4f6',
    color: '#9ca3af',
    fontSize: '0.72rem',
    padding: '0.15rem 0.5rem',
    borderRadius: '999px'
  };
  const btnLoad = {
    fontSize: '0.8rem',
    padding: '0.35rem 0.9rem',
    borderRadius: '5px',
    cursor: 'pointer',
    border: '1px solid #6366f1',
    background: '#eef2ff',
    color: '#4338ca',
    fontWeight: 600
  };
  const btnChange = {
    fontSize: '0.78rem',
    padding: '0.25rem 0.65rem',
    borderRadius: '4px',
    cursor: 'pointer',
    border: '1px solid #d1d5db',
    background: '#fff',
    color: 'var(--theme-text)',
    marginLeft: '0.5rem'
  };
  const btnSave = {
    fontSize: '0.8rem',
    padding: '0.35rem 1rem',
    borderRadius: '5px',
    cursor: 'pointer',
    border: 'none',
    background: 'var(--theme-primary-dark)',
    color: '#fff',
    fontWeight: 600
  };
  const btnCancel = {
    fontSize: '0.78rem',
    padding: '0.35rem 0.8rem',
    borderRadius: '5px',
    cursor: 'pointer',
    border: '1px solid #d1d5db',
    background: '#fff',
    color: 'var(--theme-text)'
  };

  // The API returns inactive room types too; only active ones can be mapped,
  // inactive ones are surfaced in a warning so the user knows why they're missing.
  const activeRoomTypes = pmsRoomTypes.filter(rt => rt.is_active !== false);
  const inactiveRoomTypes = pmsRoomTypes.filter(rt => rt.is_active === false);
  const mappedRtCount = activeRoomTypes.filter(rt => rt.easeroom_room_type_id).length;
  const mappedRpCount = pmsRatePlans.filter(rp => rp.easeroom_rate_plan_id).length;

  return (
    <div className="page-shell">
      {/* Breadcrumb */}
      <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '0.6rem' }}>
        <span>Front Office</span>{' '}
        <span style={{ color: '#cbd5e0' }}>›</span>{' '}
        <span>Masters</span>{' '}
        <span style={{ color: '#cbd5e0' }}>›</span>{' '}
        <span style={{ color: 'var(--theme-primary-dark)', fontWeight: 600 }}>Channel Manager Mapping</span>
      </div>
      <h2 style={{ color: 'var(--theme-primary-dark)', marginBottom: '0.15rem' }}>Channel Manager Mapping</h2>
      <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
        Map PMS Room Types and Rate Plans to their EaseRoom equivalents for inventory and rate synchronisation.
      </p>

      {error && <div className="error-banner" style={{ marginBottom: '1rem' }}>{error}</div>}
      {message && (
        <div style={{ background: '#f0fdf4', border: '1px solid #86efac', color: '#166534', padding: '0.7rem 1rem', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.875rem' }}>
          {message}
        </div>
      )}

      {loadingPms ? (
        <div style={{ color: '#6b7280', padding: '3rem', textAlign: 'center' }}>Loading PMS data...</div>
      ) : (
        <>
          {/* ===================================================
              ROOM TYPE MAPPING
          =================================================== */}
          <div style={sectionStyle}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
              <div>
                <h3 style={{ margin: 0, color: '#1e3a5f', fontSize: '1rem' }}>Room Type Mapping</h3>
                {activeRoomTypes.length > 0 && (
                  <p style={{ margin: '0.2rem 0 0', color: '#6b7280', fontSize: '0.8rem' }}>
                    <span style={{ color: mappedRtCount === activeRoomTypes.length ? '#10b981' : '#f59e0b', fontWeight: 600 }}>
                      {mappedRtCount} of {activeRoomTypes.length}
                    </span>{' '}room types mapped to EaseRoom.
                  </p>
                )}
              </div>
            </div>

            {inactiveRoomTypes.length > 0 && (
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '5px', padding: '0.5rem 0.9rem', marginBottom: '1rem', fontSize: '0.8rem', color: '#7f1d1d' }}>
                ⚠ {inactiveRoomTypes.length} room type{inactiveRoomTypes.length > 1 ? 's are' : ' is'} inactive and can't be mapped
                {' '}({inactiveRoomTypes.map(rt => rt.room_type_name).join(', ')}).
                {' '}Activate {inactiveRoomTypes.length > 1 ? 'them' : 'it'} in Front Office → Masters → Room Type (Record Active Flag).
              </div>
            )}

            {activeRoomTypes.length === 0 ? (
              <div style={{ color: '#6b7280', padding: '1.5rem', textAlign: 'center' }}>
                No active room types found for this property.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Room Type</th>
                      <th style={thStyle}>EaseRoom Mapping</th>
                      <th style={thStyle}>Status</th>
                      <th style={thStyle}>Last Mapped</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeRoomTypes.map(rt => {
                      const isOpen = activeRtId === rt.room_type_id;
                      const isMapped = Boolean(rt.easeroom_room_type_id);
                      const isLoading = loadingErRt && isOpen;

                      return (
                        <React.Fragment key={rt.room_type_id}>
                          {/* Main row */}
                          <tr style={isOpen ? { background: '#fafafa' } : {}}>
                            <td style={tdStyle}>
                              <div style={{ fontWeight: 500 }}>{rt.room_type_name}</div>
                              <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{rt.room_type_code}</div>
                            </td>
                            <td style={tdStyle}>
                              {isMapped && !isOpen ? (
                                <span style={{ fontSize: '0.85rem' }}>
                                  <span style={{ fontWeight: 500 }}>{rt.easeroom_room_type_name || '—'}</span>
                                  <span style={{ color: '#9ca3af', fontSize: '0.75rem', marginLeft: '0.4rem' }}>
                                    ID: {rt.easeroom_room_type_id}
                                  </span>
                                  <button style={btnChange} onClick={() => openRtRow(rt)}>Change</button>
                                </span>
                              ) : !isOpen ? (
                                <button
                                  style={btnLoad}
                                  disabled={loadingErRt}
                                  onClick={() => openRtRow(rt)}
                                >
                                  {loadingErRt ? 'Loading...' : 'Load from Channel Manager'}
                                </button>
                              ) : null}
                            </td>
                            <td style={tdStyle}>
                              {isMapped
                                ? <span style={badgeMapped}>✓ Mapped</span>
                                : <span style={badgeUnmapped}>Unmapped</span>}
                            </td>
                            <td style={{ ...tdStyle, fontSize: '0.75rem', color: '#6b7280' }}>
                              <div>{fmtDate(rt.last_mapped_at)}</div>
                              {rt.last_mapped_by && <div style={{ fontSize: '0.72rem' }}>{rt.last_mapped_by}</div>}
                            </td>
                          </tr>

                          {/* Inline mapping row — shown when open */}
                          {isOpen && (
                            <tr style={{ background: '#eef2ff' }}>
                              <td colSpan={4} style={{ padding: '0.75rem 1rem', borderBottom: '2px solid #c7d2fe' }}>
                                {isLoading ? (
                                  <span style={{ color: '#6366f1', fontSize: '0.85rem' }}>Loading EaseRoom room types...</span>
                                ) : erRoomTypes.length === 0 ? (
                                  <span style={{ color: '#ef4444', fontSize: '0.85rem' }}>
                                    Could not load EaseRoom room types. Check Channel Manager Settings.
                                  </span>
                                ) : (
                                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                    <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#3730a3', whiteSpace: 'nowrap' }}>
                                      Select EaseRoom Room Type:
                                    </label>
                                    <select
                                      style={selectStyle}
                                      value={rtDraft.easeroom_room_type_id}
                                      onChange={e => {
                                        const erRt = erRoomTypes.find(r => String(r.RoomTypeId) === e.target.value);
                                        setRtDraft({
                                          easeroom_room_type_id: e.target.value,
                                          easeroom_room_type_name: erRt?.RoomTypeName || ''
                                        });
                                      }}
                                    >
                                      <option value="">-- Select --</option>
                                      {erRoomTypes.map(er => (
                                        <option key={er.RoomTypeId} value={String(er.RoomTypeId)}>
                                          [{er.RoomTypeId}] {er.RoomTypeName}
                                        </option>
                                      ))}
                                    </select>
                                    <button
                                      style={btnSave}
                                      disabled={savingRtId === rt.room_type_id || !rtDraft.easeroom_room_type_id}
                                      onClick={() => saveRtMapping(rt.room_type_id)}
                                    >
                                      {savingRtId === rt.room_type_id ? 'Saving...' : 'Save'}
                                    </button>
                                    <button style={btnCancel} onClick={() => setActiveRtId(null)}>
                                      Cancel
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ===================================================
              RATE PLAN MAPPING
          =================================================== */}
          <div style={sectionStyle}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
              <div>
                <h3 style={{ margin: 0, color: '#1e3a5f', fontSize: '1rem' }}>Rate Plan Mapping</h3>
                {pmsRatePlans.length > 0 && (
                  <p style={{ margin: '0.2rem 0 0', color: '#6b7280', fontSize: '0.8rem' }}>
                    <span style={{ color: mappedRpCount === pmsRatePlans.length ? '#10b981' : '#f59e0b', fontWeight: 600 }}>
                      {mappedRpCount} of {pmsRatePlans.length}
                    </span>{' '}rate plans mapped to EaseRoom.
                  </p>
                )}
              </div>
            </div>

            <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '5px', padding: '0.5rem 0.9rem', marginBottom: '1rem', fontSize: '0.8rem', color: '#78350f' }}>
              ⚠ Map Room Types first. Rate plan options are filtered by the EaseRoom room type linked to each PMS room type.
            </div>

            {pmsRatePlans.length === 0 ? (
              <div style={{ color: '#6b7280', padding: '1.5rem', textAlign: 'center' }}>
                No active rate plans found for this property.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Rate Plan</th>
                      <th style={thStyle}>PMS Room Type</th>
                      <th style={thStyle}>EaseRoom Mapping</th>
                      <th style={thStyle}>Status</th>
                      <th style={thStyle}>Last Mapped</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pmsRatePlans.map(rp => {
                      const isOpen = activeRpId === rp.rate_plan_id;
                      const isMapped = Boolean(rp.easeroom_rate_plan_id);
                      const rtRow = pmsRoomTypes.find(rt => rt.room_type_id === rp.room_type_id);
                      const rtInactive = rp.room_type_is_active === false;
                      const rtMapped = !rtInactive && Boolean(rtRow?.easeroom_room_type_id);
                      const isLoading = loadingErRp && isOpen;
                      const filteredErRps = getFilteredErRatePlans(rp);

                      return (
                        <React.Fragment key={rp.rate_plan_id}>
                          {/* Main row */}
                          <tr style={isOpen ? { background: '#fafafa' } : {}}>
                            <td style={tdStyle}>
                              <div style={{ fontWeight: 500 }}>{rp.rate_plan_name}</div>
                              <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{rp.rate_plan_code}</div>
                            </td>
                            <td style={tdStyle}>
                              <div style={{ fontSize: '0.85rem' }}>{rp.room_type_name || '—'}</div>
                              {rtRow?.easeroom_room_type_name && (
                                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                  → {rtRow.easeroom_room_type_name}
                                </div>
                              )}
                              {rtInactive ? (
                                <div style={{ fontSize: '0.73rem', color: '#dc2626', marginTop: '0.1rem' }}>
                                  Room type inactive
                                </div>
                              ) : !rtMapped && (
                                <div style={{ fontSize: '0.73rem', color: '#f59e0b', marginTop: '0.1rem' }}>
                                  Room type not mapped
                                </div>
                              )}
                            </td>
                            <td style={tdStyle}>
                              {!rtMapped ? (
                                <span style={{ color: '#d1d5db', fontSize: '0.8rem' }}>—</span>
                              ) : isMapped && !isOpen ? (
                                <span style={{ fontSize: '0.85rem' }}>
                                  <span style={{ fontWeight: 500 }}>{rp.easeroom_rate_plan_name || '—'}</span>
                                  <span style={{ color: '#9ca3af', fontSize: '0.75rem', marginLeft: '0.4rem' }}>
                                    ID: {rp.easeroom_rate_plan_id}
                                  </span>
                                  <button style={btnChange} onClick={() => openRpRow(rp)}>Change</button>
                                </span>
                              ) : !isOpen ? (
                                <button
                                  style={btnLoad}
                                  disabled={loadingErRp}
                                  onClick={() => openRpRow(rp)}
                                >
                                  {loadingErRp ? 'Loading...' : 'Load from Channel Manager'}
                                </button>
                              ) : null}
                            </td>
                            <td style={tdStyle}>
                              {!rtMapped
                                ? <span style={{ color: '#d1d5db', fontSize: '0.78rem' }}>—</span>
                                : isMapped
                                  ? <span style={badgeMapped}>✓ Mapped</span>
                                  : <span style={badgeUnmapped}>Unmapped</span>}
                            </td>
                            <td style={{ ...tdStyle, fontSize: '0.75rem', color: '#6b7280' }}>
                              <div>{fmtDate(rp.last_mapped_at)}</div>
                              {rp.last_mapped_by && <div style={{ fontSize: '0.72rem' }}>{rp.last_mapped_by}</div>}
                            </td>
                          </tr>

                          {/* Inline mapping row — shown when open */}
                          {isOpen && (
                            <tr style={{ background: '#eef2ff' }}>
                              <td colSpan={5} style={{ padding: '0.75rem 1rem', borderBottom: '2px solid #c7d2fe' }}>
                                {isLoading ? (
                                  <span style={{ color: '#6366f1', fontSize: '0.85rem' }}>Loading EaseRoom rate plans...</span>
                                ) : erRatePlans.length === 0 ? (
                                  <span style={{ color: '#ef4444', fontSize: '0.85rem' }}>
                                    Could not load EaseRoom rate plans. Check Channel Manager Settings.
                                  </span>
                                ) : filteredErRps.length === 0 ? (
                                  <span style={{ color: '#ef4444', fontSize: '0.85rem' }}>
                                    No EaseRoom rate plans found for this room type's EaseRoom mapping.
                                    Map the room type first then try again.
                                  </span>
                                ) : (
                                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                    <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#3730a3', whiteSpace: 'nowrap' }}>
                                      Select EaseRoom Rate Plan:
                                    </label>
                                    <select
                                      style={selectStyle}
                                      value={rpDraft.easeroom_rate_plan_id}
                                      onChange={e => {
                                        const erRp = erRatePlans.find(r => String(r.RateplanID) === e.target.value);
                                        setRpDraft({
                                          easeroom_rate_plan_id: e.target.value,
                                          easeroom_rate_plan_name: erRp?.RateplanName || '',
                                          easeroom_room_type_id: erRp?.RoomTypeId ? String(erRp.RoomTypeId) : ''
                                        });
                                      }}
                                    >
                                      <option value="">-- Select --</option>
                                      {filteredErRps.map(er => (
                                        <option key={er.RateplanID} value={String(er.RateplanID)}>
                                          [{er.RateplanID}] {er.RateplanName}
                                        </option>
                                      ))}
                                    </select>
                                    <button
                                      style={btnSave}
                                      disabled={savingRpId === rp.rate_plan_id || !rpDraft.easeroom_rate_plan_id}
                                      onClick={() => saveRpMapping(rp.rate_plan_id)}
                                    >
                                      {savingRpId === rp.rate_plan_id ? 'Saving...' : 'Save'}
                                    </button>
                                    <button style={btnCancel} onClick={() => setActiveRpId(null)}>
                                      Cancel
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default PmsCmMapping;
